import React from 'react';
import { GlobeIcon, ChevronDownIcon } from 'lucide-react';
import { Logo } from '../Logo';
import { RegionSelector } from '../RegionSelector';
import { ShopConfig, AVAILABLE_LANGUAGES } from '../../data/shopConfig';
import { resolveTheme } from '../../utils/theme';
interface ShopTopBarProps {
  config: ShopConfig;
  /** When set, the header becomes the way back to the storefront. */
  onHome?: () => void;
  /** The shopper's account slot, rendered at the end of the controls. */
  account?: React.ReactNode;
}

/**
 * Shop chrome. It sits directly on the page background with no bar of its own,
 * and every mark in it is guaranteed legible: text and hairlines flip with the
 * background's luminance, and the Talab wordmark — a fixed dark asset — gets a
 * light chip only when the merchant's background is dark.
 */
export function ShopTopBar({
  config,
  onHome,
  account
}: ShopTopBarProps) {
  const t = resolveTheme(config.theme);
  const languages = AVAILABLE_LANGUAGES.filter(l => config.languages.includes(l.code));
  const active = languages.find(l => l.code === config.defaultLanguage) ?? languages[0];
  const showControls = languages.length > 1 || config.showCountrySelector;
  return <header className="relative z-40 flex items-center gap-3 lg:gap-4 px-4 lg:px-8 py-4 bg-transparent" data-id="element-4757" data-mp="9xvuehv9w65q">
      {/* Left: platform mark, or the merchant's own quiet wordmark */}
      <button type="button" onClick={onHome} disabled={!onHome} aria-label={onHome ? `Back to ${config.publicName}` : config.publicName} className={`flex items-center gap-3 min-w-0 rounded-xl -mx-1.5 px-1.5 py-1 transition-colors duration-150 ease-out ${onHome ? 'cursor-pointer' : 'cursor-default'}`} style={onHome ? undefined : {
      background: 'transparent'
    }} data-id="element-4758" data-mp="930vwsem4v">
        {config.showTalabBranding ?
      // Vector lockup, so it simply inverts on a dark shop canvas rather
      // than needing a chip to survive.
      <Logo size="sm" tone={t.darkSurface ? 'light' : 'dark'} data-id="element-4759" data-mp="2yrdo1slt87tv" /> : <span className="font-display font-bold text-[13px] uppercase tracking-[0.18em] truncate" style={{
        color: 'var(--m-on-bg)'
      }} data-id="element-4760" data-mp="33skvtrnyd70j">
            {config.publicName}
          </span>}
      </button>

      {/* Right: grouped customer controls, outlined so they read on any canvas */}
      <div className="ml-auto flex items-center gap-2.5 flex-shrink-0" data-id="element-4761" data-mp="3mhzy1pfdk26i">
      {showControls && <div className="flex items-center flex-shrink-0 rounded-full overflow-hidden" style={{
        border: '1px solid var(--m-hairline)',
        background: t.darkSurface ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.6)'
      }} data-id="element-4762" data-mp="38r4k3yq0quw2">
          {languages.length > 1 && <button className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold transition-colors duration-150 ease-out" style={{
          color: 'var(--m-on-bg)'
        }} aria-label="Change language" data-id="element-4763" data-mp="94xv2xsv40ie">
              <GlobeIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-4764" data-mp="23j20x2lsj0fl" />
              <span className="hidden sm:inline" data-id="element-4765" data-mp="1yjyx1lhjr097">{active?.nativeName}</span>
              <span className="sm:hidden" data-id="element-4766" data-mp="1auxuufzb4lcd">{active?.code.toUpperCase()}</span>
              <ChevronDownIcon className="w-3.5 h-3.5" style={{
            color: 'var(--m-on-bg-soft)'
          }} data-id="element-4767" data-mp="1ax0hqr55xycv" />
            </button>}
          {config.showCountrySelector && <div className="px-1.5 py-0.5" style={languages.length > 1 ? {
          borderLeft: '1px solid var(--m-hairline)'
        } : undefined} data-id="element-4768" data-mp="1fszass1fs4jm">
              <RegionSelector data-id="element-4769" data-mp="10sln3onh45kz" />
            </div>}
        </div>}
        {account}
      </div>
    </header>;
}